import { formatDistance } from 'date-fns'
import dayjs from '@/lib/dayjs'

/**
 * Return the parsed date string in 'MMM D, YYYY' format
 * @param {Number} seconds
 * @returns {String}
 */
export const formatDate = (seconds) => {
  return !seconds ? '' : dayjs(seconds * 1000).format('MMM D, YYYY')
}

export const formatDateFromTimestamp = (timestamp) => {
  return !timestamp ? '' : dayjs(timestamp).format('MMM D, YYYY')
}

/**
 * Return the parsed date string in 'MMM D, YYYY' format
 * @param {Date} dateObject
 * @returns {string|string}
 */
export const formatDateObject = (dateObject) => {
  return !dateObject ? '' : dayjs(dateObject).format('MMM D, YYYY')
}

/**
 * Return the parsed date string in 'MMM D, YYYY' format
 * @param {String} dateObject
 * @returns {string|string}
 */
export const formatDateString = (dateString) => {
  return !dateString ? '' : dayjs(dateString).format('MMM D, YYYY')
}

/**
 *
 * @param {String | Date} date
 * @returns Date
 *
 * Given a Date object or a Date string (eg. `'12-07-2008'`) returns a Date object in the format of `YYYY-MM-DD`
 */
export const formatDateToStandard = (date) => {
  if (date === '' || date === null) return null

  const parsed = dayjs(date)
  if (!parsed.isValid())
    throw new Error("Date(s) invalid. Please ensure you're using the correct format.")

  return parsed.format('YYYY-MM-DD')
}

/**
 *
 * @param {String | Date} date
 * @param {String} format
 * @returns Date
 *
 * Given a Date object or a Date string (eg. `'12-07-2008'`) returns a Date object in the format of `YYYY-MM-DD`
 */
export const formatDateToCustom = (date, format) => {
  if (date === '' || date === null) return

  const parsed = dayjs(date)
  if (!parsed.isValid())
    throw new Error("Date(s) invalid. Please ensure you're using the correct format.")

  return parsed.format(format)
}

/**
 * check if the date string is in the yyyy-MM-dd format
 * @param dateString date value to be checked
 * @returns Boolean
 */
export const isValidDateFormat = (dateString) => {
  return dayjs(dateString, 'YYYY-MM-DD', true).isValid()
}

export const convertFormat = (dateString, fromFormat, toFormat) => {
  return dayjs(dateString, fromFormat).format(toFormat)
}

export const getDateObject = (dateString, format) => {
  //retun standard date object
  return dayjs(dateString, format).toDate()
}

const unwrapTimestamp = (dateString) => {
  let timestamp = dateString

  if (typeof dateString === 'object') {
    if (dateString.seconds) timestamp = dateString.seconds * 1000
    else if (dateString._seconds) timestamp = dateString._seconds * 1000
  }

  return timestamp
}

export const getRelativeDate = (dateString) => {
  return formatDistance(new Date(dateString), new Date(), { addSuffix: true })
}

export const getDate = (dateString) => {
  const timestamp = unwrapTimestamp(dateString)

  return new Date(timestamp).toLocaleString()
}

/**
 * Blocks invalid characters from being inputted in a date string input field.
 *
 * @param {Event} event - The keyboard event object.
 * @returns {void}
 */
export const blockInvalidDateInputInString = (event) => {
  const allowedKeysRegex = /^[0-9-]*$/
  const keyPressed = event.key
  if (!allowedKeysRegex.test(keyPressed)) {
    event.preventDefault()
  }
}

/**
 * Returns today's date as an ISO string in YYYY-MM-DD format
 *
 * @returns {String} ISO date string (e.g., '2025-10-08')
 *
 * @example
 * getTodayIsoString() // '2025-10-08'
 */
export const getTodayIsoString = () => {
  return new Date().toISOString().split('T')[0]
}

/**
 * Normalizes effective dates by converting null/undefined to today's ISO date string
 *
 * NOTE: This function is primarily used for backward compatibility and when explicitly
 * defaulting to today's date. For display purposes, use displayEffectiveDate which
 * distinguishes between null (not set) and today's date.
 *
 * @param {String|null|undefined} rawDate - The raw date value (ISO string or null/undefined)
 * @returns {String} ISO date string in YYYY-MM-DD format
 *
 * @example
 * normalizeEffectiveDate(null)          // '2025-10-08'
 * normalizeEffectiveDate('2025-01-15')  // '2025-01-15'
 */
export const normalizeEffectiveDate = (rawDate) => {
  return rawDate || getTodayIsoString()
}

/**
 * Formats an effective date for display, distinguishing between null (not set)
 * and today's date
 *
 * @param {String|null|undefined} rawDate - The raw date value (ISO string or null/undefined)
 * @param {String} format - The date format to use (default: 'YYYY-MM-DD')
 * @returns {String} Formatted date string, 'Not Set', or 'Today'
 *
 * @example
 * displayEffectiveDate(null)              // 'Not Set'
 * displayEffectiveDate(undefined)         // 'Not Set'
 * displayEffectiveDate('2025-10-24')      // 'Today' (if today is 2025-10-24)
 * displayEffectiveDate('2025-01-15')      // '15-01-2025'
 */
export const displayEffectiveDate = (rawDate, format = 'YYYY-MM-DD') => {
  // Explicitly handle null/undefined as "Not Set"
  if (rawDate === null || rawDate === undefined || rawDate === '') {
    return 'Not Set'
  }

  const todayIso = getTodayIsoString()

  // Check if the date is today
  if (rawDate === todayIso) {
    return 'Today'
  }

  // Format other dates
  const parsed = dayjs(rawDate, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed.format(format) : rawDate
}

/**
 * Today's date in the viewer's timezone, for stamping export filenames.
 * @returns {string} Date as DD-MM-YYYY.
 */
export const formatExportDate = () => dayjs().format('DD-MM-YYYY')
